import { useUser } from '@/hooks';
import { type ZgriPermissionName } from '@/utils/types/Permission';
import { Fragment, type ReactNode } from 'react';
import { Navigate } from 'react-router';

type Permission = ZgriPermissionName;

interface BaseProps {
  require: Permission | Permission[];
  match?: 'all' | 'any';
  children: ReactNode;
}

type VisibilityProps = BaseProps & {
  variant?: 'visibility';
  fallback?: ReactNode;
  redirectTo?: never;
};

type RedirectProps = BaseProps & {
  variant?: 'redirect';
  redirectTo: string;
  fallback?: never;
};

export type ZgriPermissionGuardProps = VisibilityProps | RedirectProps;

function useUserPermissions(): Permission[] {
  const { isPublic, user } = useUser();
  if (isPublic) {
    return [];
  }

  if (!user?.userPermissions) {
    return [];
  }

  const userPermissions = user.userPermissions.reduce<Permission[]>((acc, permission) => {
    if (!permission.active) {
      return acc;
    }

    if (permission.state !== 'assigned') {
      return acc;
    }

    acc.push(permission.permission_name as Permission);

    return acc;
  }, []);

  return userPermissions;
}

export function ZgriPermissionGuard({
  children,
  require,
  fallback,
  match = 'all',
  redirectTo,
  variant = 'visibility',
}: ZgriPermissionGuardProps) {
  const userPermissions = useUserPermissions();

  const requiredPermissions = Array.isArray(require) ? require : [require];
  const allowed =
    match === 'all'
      ? requiredPermissions.every((permission) => userPermissions.includes(permission))
      : requiredPermissions.some((permission) => userPermissions.includes(permission));

  if (allowed) {
    return children;
  }

  if (variant === 'visibility') {
    return <Fragment>{fallback ?? null}</Fragment>;
  }

  return <Navigate to={redirectTo ?? '/'} replace={true} />;
}
